import { Alert } from "react-native";

import { ResourceListScreen } from "~/components/composed/ResourceListScreen";
import { Button, ButtonText } from "~/components/ui";
import { useWorkspaceReadOnly } from "~/hooks/useWorkspaceReadOnly";
import {
  usePrimaryWorkspace,
  useWorkspaceClients,
  useWorkspaceProjects,
  useWorkspaceRecurringTasks,
} from "~/hooks/workspace";
import { TASK_PRIORITY_OPTIONS } from "~/utils/work-planning";

const cadenceOptions = [
  { label: "Weekly", value: "weekly" },
  { label: "Fortnightly", value: "fortnightly" },
  { label: "Monthly", value: "monthly" },
  { label: "Every 2 months", value: "every_2_months" },
  { label: "Quarterly", value: "quarterly" },
  { label: "Yearly", value: "yearly" },
];
const yesNoOptions = [
  { label: "Yes", value: "true" },
  { label: "No", value: "false" },
];

function cadenceLabel(value: string) {
  return (
    cadenceOptions.find((option) => option.value === value)?.label ?? value
  );
}

export default function AutomationsScreen() {
  const workspace = usePrimaryWorkspace();
  const { readOnly } = useWorkspaceReadOnly(workspace.data?.id);
  const clients = useWorkspaceClients(workspace.data?.id);
  const projects = useWorkspaceProjects(workspace.data?.id);
  const recurring = useWorkspaceRecurringTasks(workspace.data?.id);
  const clientById = new Map(
    (clients.data ?? []).map((client) => [client.id, client]),
  );
  const projectById = new Map(
    (projects.data ?? []).map((project) => [project.id, project]),
  );

  const fields = [
    { key: "name", label: "Task name", placeholder: "Bi-monthly website scan" },
    {
      key: "clientId",
      label: "Client",
      placeholder: "Choose client",
      options: [
        { label: "No client", value: "" },
        ...(clients.data ?? []).map((client) => ({
          label: client.name,
          value: client.id,
        })),
      ],
      clearOnChange: ["projectId"],
    },
    {
      key: "projectId",
      label: "Project",
      placeholder: "Choose project",
      optionsForValues: (values: Record<string, string>) => [
        { label: "No project", value: "" },
        ...(projects.data ?? [])
          .filter(
            (project) =>
              !values.clientId || project.client_id === values.clientId,
          )
          .map((project) => ({ label: project.name, value: project.id })),
      ],
    },
    {
      key: "cadence",
      label: "Repeat",
      placeholder: "Monthly",
      options: cadenceOptions,
    },
    { key: "nextDueOn", label: "Next due", placeholder: "YYYY-MM-DD" },
    {
      key: "createDaysBefore",
      label: "Create days before",
      placeholder: "7",
      keyboardType: "numeric" as const,
    },
    {
      key: "priority",
      label: "Priority",
      placeholder: "Medium",
      options: TASK_PRIORITY_OPTIONS,
    },
    {
      key: "billable",
      label: "Billable",
      placeholder: "Yes",
      options: yesNoOptions,
    },
    {
      key: "dashboardNotify",
      label: "Dashboard notification",
      placeholder: "Yes",
      options: yesNoOptions,
    },
    {
      key: "clientEmailEnabled",
      label: "Email client",
      placeholder: "No",
      options: yesNoOptions,
    },
    {
      key: "clientEmailSubject",
      label: "Email subject",
      placeholder: "Your {{task_name}} is scheduled",
    },
    {
      key: "clientEmailBody",
      label: "Email message",
      placeholder: "Hi {{client_name}}, we have scheduled...",
      multiline: true,
    },
    {
      key: "description",
      label: "Internal task notes",
      placeholder: "Checklist and delivery notes",
      multiline: true,
    },
    {
      key: "enabled",
      label: "Rule enabled",
      placeholder: "Yes",
      options: yesNoOptions,
    },
  ];

  return (
    <ResourceListScreen
      title="Service automations"
      subtitle="Turn recurring client obligations into tasks, dashboard alerts, and optional client emails."
      primaryAction="Add recurring service"
      emptyTitle="Recurring services"
      emptyDescription="Add website scans, reviews, renewals, and annual services here."
      fields={fields}
      requiredField="name"
      readOnly={readOnly}
      metrics={[
        {
          label: "Active rules",
          value: String(recurring.rules.filter((rule) => rule.enabled).length),
        },
        {
          label: "Client email rules",
          value: String(
            recurring.rules.filter((rule) => rule.client_email_enabled).length,
          ),
        },
        {
          label: "Due in 30 days",
          value: String(
            recurring.rules.filter(
              (rule) =>
                rule.enabled &&
                rule.next_due_on <=
                  new Date(Date.now() + 30 * 86400000)
                    .toISOString()
                    .slice(0, 10),
            ).length,
          ),
        },
      ]}
      items={recurring.rules.map((rule) => ({
        id: rule.id,
        title: rule.name,
        description: rule.description ?? undefined,
        meta: [
          clientById.get(rule.client_id ?? "")?.name ?? "No client",
          projectById.get(rule.project_id ?? "")?.name ?? "No project",
          cadenceLabel(rule.cadence),
          `Next due ${rule.next_due_on}`,
          rule.client_email_enabled ? "Client email on" : "Dashboard only",
          rule.enabled ? "Active" : "Paused",
        ],
        editValues: readOnly
          ? undefined
          : {
              name: rule.name,
              clientId: rule.client_id ?? "",
              projectId: rule.project_id ?? "",
              cadence: rule.cadence,
              nextDueOn: rule.next_due_on,
              createDaysBefore: String(rule.create_days_before),
              priority: rule.priority,
              billable: String(rule.billable),
              dashboardNotify: String(rule.dashboard_notify),
              clientEmailEnabled: String(rule.client_email_enabled),
              clientEmailSubject: rule.client_email_subject ?? "",
              clientEmailBody: rule.client_email_body ?? "",
              description: rule.description ?? "",
              enabled: String(rule.enabled),
            },
        actions: readOnly ? undefined : (
          <Button
            compact
            variant="outline"
            disabled={recurring.remove.isPending}
            onPress={() =>
              Alert.alert(
                "Remove recurring service?",
                "Existing tasks stay in place.",
                [
                  { text: "Cancel", style: "cancel" },
                  {
                    text: "Remove",
                    style: "destructive",
                    onPress: () => recurring.remove.mutate(rule.id),
                  },
                ],
              )
            }
          >
            <ButtonText>Remove</ButtonText>
          </Button>
        ),
      }))}
      loading={
        workspace.isLoading ||
        clients.isLoading ||
        projects.isLoading ||
        recurring.isLoading
      }
      error={
        recurring.error ??
        recurring.create.error ??
        recurring.update.error ??
        recurring.remove.error ??
        recurring.runDue.error
      }
      creating={recurring.create.isPending}
      updating={recurring.update.isPending}
      onCreate={(values) => recurring.create.mutateAsync(values)}
      onUpdate={(id, values) => recurring.update.mutateAsync({ id, values })}
    />
  );
}
